const _urls = (typeof window !== 'undefined' && window.UrgencyAssetUrls) || {
  renderer: './urgency-renderer.js',
};

const { UrgencyRenderer } = await import(_urls.renderer);

// ─────────────────────────────
function readJSON(id) {
  const el = document.getElementById(id);
  if (!el) return null;
  try {
    return JSON.parse(el.textContent);
  } catch {
    return null;
  }
}

function toPayload(rule) {
  if (!rule) return null;
  return {
    rule_id: rule.rule_id || null,
    rule_name: rule.rule_name || null,
    product_id: rule.product_id || null,
    variant_id: rule.variant_id || null,
    message: rule.message,
    urgency_type: rule.urgency_type,
    placement: rule.placement,
    bg_color: rule.bg_color,
    font_color: rule.font_color,
    icon_url: rule.icon_url ? rule.icon_url : null,
    countdown_deadline: rule.countdown_deadline || null,
    fallback_used: rule.fallback_used || false,
  };
}

const UrgencyInit = {
  product: null,
  _deliveryRule: null,
  _deliveryQueue: [],

  init() {
    this.product = readJSON('urgency-product-data');

    this.evaluate();
    this._watchVariantChange();
    document.addEventListener('urgency:countdown-end', () => this._handleCountdownEnd());
  },

  _handleCountdownEnd() {
    const rule = this._deliveryRule || readJSON('urgency-rules-data');

    // For time_based and promotion urgency types, show fallback message
    if (rule && (rule.urgency_type === 'time_based' || rule.urgency_type === 'promotion')) {
      this._renderFallback();
    } else {
      // For delivery urgency, advance to next delivery slot
      this._advanceDelivery();
    }
  },

  _clearNow() {
    this._deliveryRule = null;
    this._deliveryQueue = [];
    try {
      UrgencyRenderer.render(null, this.product);
    } catch {
      /* renderer not ready yet — slots are still empty, nothing to do */
    }
  },

  _selectedVariantId() {
    const el = document.querySelector('[name="id"]');
    return el && el.value ? String(el.value) : null;
  },

  evaluate() {
    try {
      this.product = readJSON('urgency-product-data') || this.product;

      const selectedId = this._selectedVariantId();
      const renderedId = this.product && this.product.variant_id != null ? String(this.product.variant_id) : null;
      if (selectedId && renderedId && selectedId !== renderedId) {
        this._clearNow();
        return;
      }

      const rule = readJSON('urgency-rules-data');

      if (rule && Array.isArray(rule.deliveries)) {
        this._startDelivery(rule);
        return;
      }

      this._deliveryRule = null;
      this._deliveryQueue = [];
      UrgencyRenderer.render(toPayload(rule), this.product);
    } catch {
      UrgencyRenderer.render(null, this.product);
    }
  },

  _startDelivery(rule) {
    this._deliveryRule = rule;

    const now = Date.now();
    this._deliveryQueue = rule.deliveries
      .filter((d) => Number(d.countdown_deadline) > now)
      .sort((a, b) => a.countdown_deadline - b.countdown_deadline);

    this._renderCurrentDelivery();
  },

  _renderCurrentDelivery() {
    const entry = this._deliveryQueue[0];
    if (!entry) {
      this._renderFallback();
      return;
    }
    const payload = toPayload({
      ...this._deliveryRule,
      message: entry.message,
      countdown_deadline: entry.countdown_deadline,
    });
    UrgencyRenderer.render(payload, this.product);
  },

  _advanceDelivery() {
    if (!this._deliveryRule) return;
    this._deliveryQueue.shift();
    this._renderCurrentDelivery();
  },

  _renderFallback() {
    const rule = this._deliveryRule || readJSON('urgency-rules-data');
    if (!rule || !rule.fallback_message) {
      UrgencyRenderer.render(null, this.product);
      return;
    }
    const payload = toPayload({
      ...rule,
      message: rule.fallback_message,
      countdown_deadline: null,
      fallback_used: true,
    });
    UrgencyRenderer.render(payload, this.product);
  },

  _watchVariantChange() {
    let lastVariantId = this.product && this.product.variant_id;

    if (typeof window !== 'undefined' && typeof window.subscribe === 'function') {
      window.subscribe('option-value-selection-change', () => this._clearNow());
    }

    // Method 1: variant selector
    const variantSelect = document.querySelector('[name="id"]');
    if (variantSelect) {
      variantSelect.addEventListener('change', (e) => {
        const newVariantId = e.target.value;
        if (newVariantId === lastVariantId) return;
        lastVariantId = newVariantId;
        if (this.product) this.product.variant_id = newVariantId;
        this._clearNow();
        this.evaluate();
      });
    }

    // Method 2: URL param change
    window.addEventListener('popstate', () => {
      const variantId = new URLSearchParams(window.location.search).get('variant');
      if (variantId && variantId !== lastVariantId) {
        lastVariantId = variantId;
        if (this.product) this.product.variant_id = variantId;
        this._clearNow();
        this.evaluate();
      }
    });
  },
};

if (typeof window !== 'undefined') window.UrgencyInit = UrgencyInit;

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => UrgencyInit.init());
} else {
  UrgencyInit.init();
}
